/** @file Canonical JSON: sorted object keys, no whitespace, -0 as 0; the text bundle and document hashes use. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';

const escapeKey = (k: string): string => k.replace(/~/g, '~0').replace(/\//g, '~1');

function writeNumber(value: number, path: string): string {
  if (!Number.isFinite(value)) return throwError(ERROR_CODES.VALIDATION, { value: String(value) }, { path: path || '/', hint: 'finite numbers only' });
  return Object.is(value, -0) ? '0' : JSON.stringify(value);
}

function writeComposite(value: object | null, path: string): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return `[${value.map((v: unknown, i) => write(v, `${path}/${i}`) ?? 'null').join(',')}]`;
  return writeObject(value as Record<string, unknown>, path);
}

const notJson = (value: unknown, path: string): never =>
  throwError(ERROR_CODES.VALIDATION, { type: typeof value }, { path: path || '/', hint: 'JSON values only' });

/** Writer per `typeof`; undefined means "skip this field". */
const WRITERS: Record<string, (value: never, path: string) => string | undefined> = {
  string: (v: string) => JSON.stringify(v),
  boolean: (v: boolean) => JSON.stringify(v),
  number: writeNumber,
  undefined: () => undefined,
  object: writeComposite,
};

function write(value: unknown, path: string): string | undefined {
  const writer = WRITERS[typeof value] ?? notJson;
  return writer(value as never, path);
}

function writeObject(value: Record<string, unknown>, path: string): string {
  const parts: string[] = [];
  for (const key of Object.keys(value).sort()) {
    const text = write(value[key], `${path}/${escapeKey(key)}`);
    if (text !== undefined) parts.push(`${JSON.stringify(key)}:${text}`);
  }
  return `{${parts.join(',')}}`;
}

/**
 * `value` as canonical JSON: object keys sorted (code unit order), no whitespace, `-0` written `0`, undefined fields
 * skipped; a non-finite number or a non-JSON value throws `VALIDATION` at its JSON pointer (R4: the server imports it).
 */
export function canonicalJson(value: unknown): string {
  return write(value, '') ?? 'null';
}
